var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { WHITE } from '../../constants/colors';
import { CheckboxIcon, SubtractIcon } from '../../icons';
import { StateConfigContext } from '../../providers';
import { getTestId } from '../../utils';
import { Button } from '../button';
import { InfoTag } from '../info-tag';
import { Typography } from '../typography';
import { getCheckboxProps, getCheckboxStyles, getCheckboxTextProps } from './CheckboxStyles';
export var Checkbox = forwardRef((props, ref) => {
  var _a, _b, _c;
  var stateContext = useContext(StateConfigContext);
  var checkboxProps = getCheckboxProps(props, stateContext);
  var stylesBasedOnState = useRef();
  var label = checkboxProps.label,
    description = checkboxProps.description,
    actionCta = checkboxProps.actionCta,
    size = checkboxProps.size,
    disabled = checkboxProps.disabled,
    checked = checkboxProps.checked,
    controlState = checkboxProps.controlState,
    indeterminate = checkboxProps.indeterminate,
    onActionCtaPress = checkboxProps.onActionCtaPress,
    value = checkboxProps.value,
    style = checkboxProps.style,
    _onPress = checkboxProps.onPress,
    _checkboxProps$testID = checkboxProps.testID,
    testID = _checkboxProps$testID === void 0 ? 'Checkbox' : _checkboxProps$testID,
    showInfoTag = checkboxProps.showInfoTag,
    infoTagProps = checkboxProps.infoTagProps,
    multiTextLabel = checkboxProps.multiTextLabel,
    showExpandCta = checkboxProps.showExpandCta,
    expandCtaData = checkboxProps.expandCtaData,
    restProps = __rest(checkboxProps, ["label", "description", "actionCta", "size", "disabled", "checked", "controlState", "indeterminate", "onActionCtaPress", "value", "style", "onPress", "testID", "showInfoTag", "infoTagProps", "multiTextLabel", "showExpandCta", "expandCtaData"]);
  var _ref = expandCtaData || {},
    _ref$numberOfLines = _ref.numberOfLines,
    numberOfLines = _ref$numberOfLines === void 0 ? 2 : _ref$numberOfLines,
    _ref$expandedCtaTitle = _ref.expandedCtaTitle,
    expandedCtaTitle = _ref$expandedCtaTitle === void 0 ? 'read less' : _ref$expandedCtaTitle,
    _ref$collapseCtaTitle = _ref.collapseCtaTitle,
    collapseCtaTitle = _ref$collapseCtaTitle === void 0 ? 'read more' : _ref$collapseCtaTitle,
    onExpandCtaPress = _ref.onExpandCtaPress,
    accessibilityProps = _ref.accessibilityProps,
    expandCtaProps = _ref.expandCtaProps,
    _ref$initialExpanded = _ref.initialExpanded,
    initialExpanded = _ref$initialExpanded === void 0 ? false : _ref$initialExpanded;
  var _useState = useState(initialExpanded),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var getPressableStyle = useCallback(event => {
    var styles = getCheckboxStyles(checkboxProps, event);
    stylesBasedOnState.current = styles;
    return [styles.container, style];
  }, [checkboxProps]);
  var iconSize = (_c = (_b = (_a = checkboxProps.config) === null || _a === void 0 ? void 0 : _a.size) === null || _b === void 0 ? void 0 : _b[size]) === null || _c === void 0 ? void 0 : _c.iconSize;
  var showIcon = checked;
  var Icon = indeterminate ? SubtractIcon : CheckboxIcon;
  var hasLabelDescActionCta = label || description || actionCta || multiTextLabel;
  return _jsx(Pressable, Object.assign({
    accessibilityRole: "checkbox",
    ref: ref,
    style: getPressableStyle,
    disabled: disabled || controlState == 'disabled',
    onPress: event => {
      var data = {
        event,
        value,
        checked,
        label
      };
      _onPress === null || _onPress === void 0 ? void 0 : _onPress(data);
    },
    accessibilityState: {
      selected: checked,
      disabled: disabled || controlState == 'disabled'
    }
  }, restProps, getTestId(testID), {
    children: event => {
      var checkboxTextProps = getCheckboxTextProps(event, checkboxProps);
      return _jsxs(_Fragment, {
        children: [_jsx(View, Object.assign({
          style: stylesBasedOnState.current.iconContainer
        }, {
          children: showIcon && _jsx(Icon, Object.assign({
            fontSize: iconSize,
            stroke: WHITE,
            color: WHITE
          }, getTestId(testID, 'icon')))
        })), hasLabelDescActionCta && _jsxs(View, Object.assign({
          style: stylesBasedOnState.current.checkboxTextContainer
        }, {
          children: [_jsxs(View, Object.assign({
            style: stylesBasedOnState.current.checkboxTextAndLabel
          }, {
            children: [(!!label || (multiTextLabel === null || multiTextLabel === void 0 ? void 0 : multiTextLabel.length)) && _jsx(Typography, Object.assign({
              text: label
            }, !!(multiTextLabel === null || multiTextLabel === void 0 ? void 0 : multiTextLabel.length) && {
              multiText: multiTextLabel
            }, {
              type: checkboxTextProps.labelFontType,
              color: checkboxTextProps.labelColor
            }, !isExpanded && {
              numberOfLines
            }, getTestId(testID, "label"))), showInfoTag && _jsx(View, Object.assign({
              style: stylesBasedOnState.current.infoTagContainer
            }, {
              children: _jsx(InfoTag, Object.assign({}, infoTagProps, getTestId(testID, 'showInfoTag')))
            }))]
          })), !!description && _jsx(Typography, Object.assign({
            text: description,
            type: checkboxTextProps.descriptionFontType,
            color: checkboxTextProps.descriptionColor
          }, getTestId(testID, "description"))), showExpandCta && _jsx(Button, Object.assign({
            size: "sm"
          }, expandCtaProps, {
            title: isExpanded ? expandedCtaTitle : collapseCtaTitle,
            buttonType: "tertiary",
            onPress: () => {
              setIsExpanded(isExpanded => !isExpanded);
              onExpandCtaPress === null || onExpandCtaPress === void 0 ? void 0 : onExpandCtaPress({
                isExpanded,
                expandCtaData
              });
            },
            style: stylesBasedOnState.current.actionCta
          }, accessibilityProps, getTestId(testID, "expandCta"))), !!actionCta && _jsx(Button, Object.assign({
            size: "sm",
            title: actionCta,
            buttonType: "tertiary",
            onPress: onActionCtaPress,
            style: stylesBasedOnState.current.actionCta
          }, getTestId(testID, "actionCta")))]
        }))]
      });
    }
  }));
});